import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bookmark, Search, Trash2 } from 'lucide-react';

import type { SavedFilter } from '@tasks/contracts';

import { searchIssues } from '../../api/issues';
import { createSavedFilter, deleteSavedFilter, listSavedFilters } from '../../api/filters';
import { validateJql } from '../../api/jql';
import { queryKeys } from '../../api/queryKeys';
import { Button } from '../../components/Button';
import { SaveFilterDialog } from './SaveFilterDialog';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Badge } from '../../components/Badge';
import { IconButton } from '../../components/IconButton';
import { Input } from '../../components/Input';
import { Page, PageHeader } from '../../components/Page';
import { Skeleton } from '../../components/Skeleton';
import { SearchResults } from './SearchResults';
import { useToast } from '../../components/Toast';
import { humanizeError } from '../../lib/errors';
import { useSearchColumns } from './useSearchColumns';
import { highlightJql, sortIssues, toggleSort, type SortState } from './searchModel';
import styles from './SearchPage.module.css';

const PAGE_SIZE = 50;

/** `/search?jql=…` — JQL, валидация, таблица результатов и сохранённые фильтры. */
export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const toast = useToast();

  const jql = searchParams.get('jql') ?? '';
  const offset = Number(searchParams.get('offset') ?? '0');

  const [draft, setDraft] = useState(jql);
  const [sort, setSort] = useState<SortState | null>(null);
  const [isSaveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveShared, setSaveShared] = useState(false);
  const [filterToDelete, setFilterToDelete] = useState<SavedFilter | null>(null);

  const validationQuery = useQuery({
    queryKey: queryKeys.jqlValidate(draft),
    queryFn: ({ signal }) => validateJql(draft, signal),
    enabled: draft.trim().length >= 3,
    retry: false,
  });

  const resultsQuery = useQuery({
    queryKey: queryKeys.issues.search(jql, offset),
    queryFn: ({ signal }) => searchIssues({ jql, limit: PAGE_SIZE, offset }, signal),
    enabled: jql.trim().length > 0,
  });

  const filtersQuery = useQuery({
    queryKey: queryKeys.filters,
    queryFn: ({ signal }) => listSavedFilters(signal),
  });

  const saveMutation = useMutation({
    mutationFn: () => createSavedFilter({ name: saveName.trim(), jql, projectId: null, isShared: saveShared }),
    onSuccess: () => {
      setSaveOpen(false);
      setSaveName('');
      setSaveShared(false);
      void queryClient.invalidateQueries({ queryKey: queryKeys.filters });
      toast.success('Фильтр сохранён');
    },
    onError: (cause: unknown) => {
      toast.error(humanizeError(cause).message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteSavedFilter(id),
    onSuccess: () => {
      setFilterToDelete(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.filters });
    },
    onError: (cause: unknown) => {
      toast.error(humanizeError(cause).message);
    },
  });

  const runSearch = useCallback(() => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (draft.trim().length === 0) {
          next.delete('jql');
        } else {
          next.set('jql', draft.trim());
        }
        next.delete('offset');
        return next;
      },
      { replace: true },
    );
  }, [draft, setSearchParams]);

  const goToOffset = useCallback(
    (nextOffset: number) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          if (nextOffset === 0) {
            next.delete('offset');
          } else {
            next.set('offset', String(nextOffset));
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const rows = useMemo(() => sortIssues(resultsQuery.data?.items ?? [], sort), [resultsQuery.data?.items, sort]);

  const columns = useSearchColumns();

  const validationError = validationQuery.data?.error ?? null;
  const total = resultsQuery.data?.total ?? 0;

  return (
    <Page width="wide">
      <PageHeader title="Поиск" />

      <div className={styles['jqlBlock']}>
        <div className={styles['jqlRow']}>
          <div className={styles['jqlInput']}>
            <Input
              label="JQL-запрос"
              name="jql"
              value={draft}
              placeholder="project = DEMO AND status != Done ORDER BY rank"
              error={validationError ? validationError.message : null}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  runSearch();
                }
              }}
            />
          </div>
          <Button variant="primary" icon={<Search size={14} />} onClick={runSearch}>
            Найти
          </Button>
          <Button
            variant="secondary"
            icon={<Bookmark size={14} />}
            disabled={jql.trim().length === 0}
            onClick={() => setSaveOpen(true)}
          >
            Сохранить фильтр
          </Button>
        </div>

        {validationError && validationError.position >= 0 ? (
          <pre className={styles['jqlError']}>{renderJqlWithMarker(draft, validationError.position)}</pre>
        ) : null}

        {validationQuery.data?.fields && validationQuery.data.fields.length > 0 ? (
          <p className={styles['fieldsHint']}>Доступные поля: {validationQuery.data.fields.join(', ')}</p>
        ) : null}
      </div>

      <div className={styles['layout']}>
        <SearchResults
          jql={jql}
          rows={rows}
          columns={columns}
          total={total}
          offset={offset}
          sort={sort}
          isPending={resultsQuery.isPending}
          isError={resultsQuery.isError}
          error={resultsQuery.error}
          onRetry={() => void resultsQuery.refetch()}
          onSortChange={(columnId) => setSort((current) => toggleSort(current, columnId))}
          onOffsetChange={goToOffset}
        />

        <aside className={styles['filters']} aria-label="Сохранённые фильтры">
          <h2 className={styles['filtersTitle']}>Сохранённые фильтры</h2>
          {filtersQuery.isPending ? (
            <Skeleton variant="row" count={4} />
          ) : (filtersQuery.data ?? []).length === 0 ? (
            <p className={styles['filtersEmpty']}>Сохранённых фильтров нет. Сохраните текущий запрос.</p>
          ) : (
            <ul className={styles['filtersList']}>
              {(filtersQuery.data ?? []).map((filter) => (
                <li key={filter.id} className={styles['filterItem']}>
                  <button
                    type="button"
                    className={styles['filterName']}
                    onClick={() => {
                      setDraft(filter.jql);
                      setSearchParams({ jql: filter.jql }, { replace: true });
                    }}
                  >
                    {filter.name}
                  </button>
                  {filter.isShared ? <Badge tone="primary">команде</Badge> : null}
                  <IconButton
                    size="sm"
                    tone="danger"
                    label={`Удалить фильтр ${filter.name}`}
                    icon={<Trash2 size={14} />}
                    onClick={() => setFilterToDelete(filter)}
                  />
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>

      <SaveFilterDialog
        open={isSaveOpen}
        name={saveName}
        isShared={saveShared}
        isPending={saveMutation.isPending}
        onNameChange={setSaveName}
        onSharedChange={setSaveShared}
        onClose={() => setSaveOpen(false)}
        onSave={() => saveMutation.mutate()}
      />

      <ConfirmDialog
        open={filterToDelete !== null}
        title="Удалить фильтр?"
        description="Фильтр исчезнет из списка у всех, кому он доступен."
        confirmLabel="Удалить"
        isPending={deleteMutation.isPending}
        onConfirm={() => {
          if (filterToDelete) {
            deleteMutation.mutate(filterToDelete.id);
          }
        }}
        onClose={() => setFilterToDelete(null)}
      />
    </Page>
  );
}

/** Подсветка позиции синтаксической ошибки в JQL (UC-SRC-02). */
function renderJqlWithMarker(jql: string, position: number): ReactNode {
  const parts = highlightJql(jql, position);
  return (
    <>
      {parts.before}
      <mark>{parts.marker}</mark>
      {parts.after}
    </>
  );
}
